import { ELEMENT_CONFIG } from "../config";
import {
  HTML_TAGS_LIST,
  HTML_CLASSES_LIST,
  DEFAULT_CLASSES,
  ELEMENT_KEYS,
} from "../lib/constants";
import slugify from "slugify";
import dayjs from "dayjs";
import hljs from "highlight.js";
import markdown from "markdown-it";
import _ from "lodash";
import { twMerge } from "tailwind-merge";

const md = markdown({
  html: true,
  linkify: true,
  breaks: true,
  xhtmlOut: true,
  typographer: true,
  highlight: function (str, lang) {
    if (lang && hljs.getLanguage(lang)) {
      try {
        return hljs.highlight(lang, str).value;
      } catch (__) {}
    }

    return ""; // use external default escaping
  },
});

md.renderer.rules.table_open = () => {
  return '<div class="table-wrapper"><table>';
};

md.renderer.rules.table_close = () => {
  return "</table></div>";
};

const generatePlatforms = (
  platforms,
  clses = {
    title: "title",
    content: "content",
  },
  props = {},
) => {
  platforms = [].concat(platforms);

  const layout = [
    {
      key: clses.title,
    },
    {
      key: clses.content,
    },
    {
      type: "media",
      key: "files",
    },
    {
      key: "brand",
    },
  ];

  const PLATFORMS = [
    {
      label: "Instagram Post (1080x1350)",
      platform: "instagram",
      containerWidth: 1080,
      containerHeight: 1350,
      className: `h-[1350px] w-[1080px]`,
    },
    {
      label: "Instagram Story (1080x1920)",
      platform: "instagram_story",
      containerWidth: 1080,
      containerHeight: 1920,
      className: `h-[1920px] w-[1080px]`,
    },
    {
      label: "Twitter (1200x675)",
      platform: "twitter",
      containerWidth: 1200,
      containerHeight: 675,
      className: `h-[675px] w-[1200px]`,
    },
    {
      label: "Facebook Post (1200x630)",
      platform: "facebook_post",
      containerWidth: 1200,
      containerHeight: 630,
      className: `h-[630px] w-[1200px]`,
    },
    {
      label: "LinkedIn Post (1200x627)",
      platform: "linkedin_post",
      containerWidth: 1200,
      containerHeight: 627,
      className: `h-[627px] w-[1200px]`,
    },
    // {
    //   platform: "youtube_thumbnail",
    //   containerWidth: 1280,
    //   containerHeight: 720,
    //   className: `h-[720px] w-[1280px]`,
    //   layout,
    // },
  ];

  return PLATFORMS.filter((page) =>
    platforms[0] === "all" ? true : platforms.includes(page.platform),
  ).map((page) => {
    return {
      ...page,
      layout: layout.filter((layout) => layout.key !== null),
      ...props,
    };
  });
};

const getFormattedDate = () => {
  return dayjs().format("YYYY-MM-DD");
};

const getSlug = (str) =>
  slugify(str, {
    lower: true,
    strict: true,
  });

const getHtmlClassName = (classList) => {
  for (const className of classList) {
    if (HTML_CLASSES_LIST.includes(className)) {
      return className;
    }
  }
};

const isHtmlTag = (selectedElement) =>
  HTML_TAGS_LIST.includes(splitElementKey(selectedElement)["element"]) ||
  HTML_TAGS_LIST.includes(selectedElement);

const splitElementKey = (selectedElement) => {
  const [pageNo, element, uid] = selectedElement.split(":");
  return {
    pageNo,
    element,
    uid,
  };
};

const generateElementKey = (pageNo, element, uid) =>
  `${pageNo || 0}:${element}:${uid || ""}`;

// Removes '_' and '.' from key
const cleanElementName = (key) => {
  const cleaned = key.includes("_") ? key.split("_")[0] : key;
  return cleaned.replace(/\./g, "");
};

const getPropertyDefaultValue = (key, property) => {
  const { element } = splitElementKey(property);
  const defaultValue = _.get(DEFAULT_CLASSES, [cleanElementName(element), key]);
  // console.log({ element, key, defaultValue });

  return defaultValue;
};

const getBase64 = (file, callback) => {
  const reader = new FileReader();
  reader.addEventListener("load", () => callback(reader.result));
  reader.readAsDataURL(file);
};

const getCustomParsingElement = (element, classList = []) => {
  const elementValues = Object.values(ELEMENT_CONFIG);

  for (const item of [element, ...classList]) {
    for (const { tags = [], customParsingElement } of elementValues) {
      if (tags.includes(item)) return customParsingElement;
    }
  }
};

const getElementName = (tag = "", classList = []) => {
  classList = [...classList].map((cls) => `.${cls}`);

  const customParsingElement = getCustomParsingElement(tag, classList);
  if (customParsingElement) {
    return {
      elementName: customParsingElement,
      match: "custom",
    };
  }

  const htmlClassName = getHtmlClassName(classList) || getHtmlClassName([tag]);
  if (htmlClassName) {
    return {
      elementName: htmlClassName,
      match: "class",
    };
  }

  if (isHtmlTag(tag)) {
    return {
      elementName: tag,
      match: "tag",
    };
  }

  return null;
};

const stringifyClassNames = (obj = {}, classes) => {
  const objClasses = Object.values(obj);
  const otherClasses =
    typeof classes === "string" ? classes.split(" ") : classes;

  const final = [...objClasses, ...otherClasses].join(" ");
  return twMerge(final);
};

const generateRootId = (pageNo) =>
  generateElementKey(pageNo, `.${ELEMENT_KEYS.RAW_ROOT_EDITOR}`);

export {
  isHtmlTag,
  splitElementKey,
  generateElementKey,
  generatePlatforms,
  cleanElementName,
  getSlug,
  getFormattedDate,
  md,
  getHtmlClassName,
  getPropertyDefaultValue,
  getBase64,
  getElementName,
  stringifyClassNames,
  generateRootId,
};
